import {
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Clock, Database } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import backend from '../../../services/backend';
import { useHorariosAgendamento } from '../../../services/hooks/useHorariosAgendamento';
import InputText from '../../shared/Inputs/InputText';

const FormSchema = z.object({
  data: z.string().nonempty('Data Obrigatória'),
  hora: z.string().nonempty('Hora Obrigatória'),
});

interface ICertificadoModal {
  isOpen: boolean;
  close(): void;
  idCertificado: string; // uuid
  reagendamento?: boolean;
}

const AgendamentoModal: React.FC<ICertificadoModal> = ({
  isOpen,
  close,
  idCertificado, // uuid
  reagendamento = false,
}) => {
  // estilos
  const color = useColorModeValue('#2D3748', 'white');
  const color2 = useColorModeValue('#3f3f46', '#e4e4e7');
  const bg = useColorModeValue('white', 'gray.900');

  // consts
  const dataHoje = new Date();
  const formattedToday = dataHoje.toISOString().split('T')[0]; // YYYY-MM-DD
  const Toast = useToast();
  const history = useHistory();

  type formData = z.infer<typeof FormSchema>;
  const { handleSubmit, watch, setValue } = useForm<formData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      data: formattedToday,
      hora: '',
    },
  });

  // watchs
  const data = watch('data');
  const hora = watch('hora');

  // estados
  const [enviando, setEnviando] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // dados
  const {
    data: HorariosData,
    isLoading,
    error,
  } = useHorariosAgendamento(data, idCertificado);

  useEffect(() => {
    if (isOpen && error) {
      Toast({
        title: error.response.data,
        position: 'bottom-right',
        isClosable: true,
        duration: 5000,
      });
      // close();
    }
  }, [error, isOpen]);

  const horas = HorariosData ?? [];

  const MarcaAgendamento = async (dados: formData) => {
    if (enviando) return;
    setEnviando(true);
    try {
      const response = await backend.post('/agenda', {
        uuid: idCertificado,
        data: dados?.data,
        hora: dados?.hora,
      });
      Toast({
        title: reagendamento
          ? 'Reagendado com sucesso'
          : 'Agendado com sucesso',
        isClosable: true,
        status: 'success',
        position: 'bottom-right',
      });
      close();
      history.replace(`/certificados/detalhes/${idCertificado}`);
      window.location.reload();
    } catch (error) {
      //
      Toast({
        title: 'Erro ao agendar',
        description: error?.response?.data,
        isClosable: true,
        status: 'error',
        position: 'bottom-right',
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={close} bg={bg}>
      <ModalOverlay>
        <ModalContent borderRadius="xl">
          <ModalHeader color={color}>
            {reagendamento ? 'Reagendamento' : 'Agendamento'}
          </ModalHeader>{' '}
          <ModalCloseButton color={color} />
          <ModalBody>
            <form
              onSubmit={handleSubmit(MarcaAgendamento, errors => {
                Toast({
                  title: 'Validação',
                  description: 'Preencha os campos destacados.',
                  status: 'error',
                  duration: 4000,
                  position: 'bottom-right',
                  isClosable: true,
                });
              })}
            >
              {error && (
                <>
                  <Text color={color}>{error?.response?.data}</Text>
                </>
              )}
              {!error && (
                <>
                  <Text color={color2}>
                    Selecione um dia para visualizar os horários disponiveis
                    para a videoconferencia
                  </Text>
                  <InputText
                    mt="2"
                    type="date"
                    value={data}
                    onChange={e => setValue('data', e.target.value)}
                  />
                  <Flex
                    p="2"
                    my="2"
                    justify="center"
                    maxH="300px"
                    overflowY="auto"
                  >
                    {isLoading && <Spinner color="blue.500" />}
                    {!isLoading && (
                      <SimpleGrid
                        templateColumns={['1fr', '1fr', '1fr 1fr 1fr 1fr']}
                        w="100%"
                        gap="4"
                        // columnGap="2"
                        rowGap="2"
                      >
                        {horas?.map(item => {
                          return (
                            <Flex
                              borderWidth="2px"
                              justify="center"
                              p="1"
                              rounded="2xl"
                              cursor="pointer"
                              onClick={() => setValue('hora', item)}
                              borderColor={
                                item === hora ? 'blue.500' : 'inherit'
                              }
                              bg={item === hora ? 'blue.500' : 'inherit'}
                            >
                              <Text color={color2}>{item}</Text>
                            </Flex>
                          );
                        })}
                      </SimpleGrid>
                    )}
                  </Flex>
                </>
              )}
              <Flex justify="flex-end" align="flex-end" flexDir="column">
                <Flex align="center" justify="center">
                  <Clock color={color2} />
                  <Text ml="1" fontWeight="medium" color={color2}>
                    Horário de Brasília - DF
                  </Text>
                </Flex>
                <Flex mt="2">
                  <Button variant="outline" onClick={close}>
                    Cancelar
                  </Button>
                  <Button
                    ml="2"
                    variant="outline"
                    onClick={() => {
                      close();
                      history.replace(
                        `/certificados/detalhes/${idCertificado}`,
                      );
                      window.location.reload();
                    }}
                  >
                    Pular
                  </Button>
                  <Button
                    ml="2"
                    type="submit"
                    disabled={hora === '' || enviando}
                  >
                    {enviando ? (
                      <>
                        <Spinner mr="2" />
                        {reagendamento ? 'Reagendando' : 'Agendando'}
                      </>
                    ) : reagendamento ? (
                      'Reagendar'
                    ) : (
                      'Agendar'
                    )}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default AgendamentoModal;
